import { ref } from 'vue';
import { auth, db } from '@/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { uploadFile } from '@/utils/storage';

export function useProfilePhoto() {
  const isUploading = ref(false);
  const uploadError = ref<string | null>(null);
  const uploadSuccess = ref(false);

  /**
   * Validate image file
   */
  const validateImage = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Please upload a valid image file (JPG, PNG, or WEBP)',
      };
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Image size must be less than 5MB',
      };
    }

    return { valid: true };
  };

  /**
   * Upload profile photo
   */
  const uploadProfilePhoto = async (file: File): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      isUploading.value = true;
      uploadError.value = null;
      uploadSuccess.value = false;

      // Get current user
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Validate image
      const validation = validateImage(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Generate unique filename
      const timestamp = Date.now();
      const extension = file.name.split('.').pop();
      const fileName = `profile_${timestamp}.${extension}`;

      // Upload to Firebase Storage
      const uploadPath = `users/${user.uid}/profile-photo/`;
      const result = await uploadFile(file, uploadPath, fileName);

      if (!result.success || !result.url) {
        throw new Error(result.error || 'Failed to upload image');
      }

      // Update Firestore user document
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        profileImage: result.url,
        updatedAt: new Date(),
      });

      console.log('✅ Profile photo uploaded successfully:', result.url);

      uploadSuccess.value = true;
      isUploading.value = false;

      return {
        success: true,
        url: result.url,
      };
    } catch (error: any) {
      console.error('❌ Error uploading profile photo:', error);
      uploadError.value = error.message || 'Failed to upload photo';
      isUploading.value = false;

      return {
        success: false,
        error: error.message || 'Failed to upload photo',
      };
    }
  };

  /**
   * Open file picker and upload
   */
  const selectAndUploadPhoto = (): Promise<{ success: boolean; url?: string; error?: string }> => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/jpg,image/png,image/webp';

      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];

        if (!file) {
          resolve({ success: false, error: 'No file selected' });
          return;
        }

        const result = await uploadProfilePhoto(file);
        resolve(result);
      };

      input.oncancel = () => {
        resolve({ success: false, error: 'Upload cancelled' });
      };

      input.click();
    });
  };

  return {
    isUploading,
    uploadError,
    uploadSuccess,
    uploadProfilePhoto,
    selectAndUploadPhoto,
  };
}

